import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Crown, X, ShieldCheck, Sparkle, Users } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { usePremiumModalStore } from '@/stores/usePremiumModalStore'
import { supabase } from '@/lib/supabase'
import { getReferralInfo } from '@/services/userService'

export function PremiumUpsellModal() {
  const { isOpen, settings, closeModal } = usePremiumModalStore()
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const [userRecords, setUserRecords] = useState<{ referralCode: string | null; totalReferrals: number }>({
    referralCode: null,
    totalReferrals: 0,
  })

  useEffect(() => {
    if (isOpen) {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user?.id) {
          getReferralInfo(user.id).then(bilgi => {
            if (bilgi) setUserRecords(bilgi)
          })
        }
      })
    }
  }, [isOpen])

  const referralCode = userRecords.referralCode
  const totalReferrals = userRecords.totalReferrals



  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-[100] bg-[#FCFAF8]/80 backdrop-blur-md"
          />

          {/* Bottom sheet for mobile, centered for desktop */}
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center pointer-events-none p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="w-full sm:max-w-sm bg-white rounded-t-[32px] sm:rounded-[32px] shadow-[0_-8px_30px_rgb(0,0,0,0.08)] sm:shadow-2xl overflow-hidden pointer-events-auto border-t sm:border border-emerald-100 relative"
            >
              {/* Header Gradient - Light Premium */}
              <div className="relative h-28 bg-gradient-to-br from-emerald-100 relative via-white to-emerald-50/50 flex items-center justify-center overflow-hidden border-b border-emerald-100">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]" />
                <motion.div
                  initial={{ rotate: -10, scale: 0.9 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5, duration: 0.8 }}
                  className="w-14 h-14 bg-white/80 backdrop-blur-md rounded-2xl flex items-center justify-center border border-emerald-200/50 shadow-md shadow-emerald-500/10"
                >
                  <Crown className="w-7 h-7 text-emerald-500" />
                </motion.div>

                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center text-slate-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 text-center">
                <h3 className="text-[22px] font-extrabold tracking-tight text-slate-900 mb-2">
                  Premium ile Sınırları Aşın!
                </h3>

                <p className="text-[14.5px] text-slate-600 mb-7 leading-relaxed font-medium">
                  {settings.message || "Sosyal ağınız giderek büyüyor! Çalışma arkadaşlarınız, dostlarınız ve tüm sevdiklerinizle sınır olmadan sosyalleşmeye devam etmek için Premium'a geçin."}
                </p>

                <div className="space-y-3 mb-8">
                  {/* 1. Sınırsız Grup */}
                  <div className="flex items-center gap-3.5 text-left bg-slate-50 p-2.5 rounded-2xl border border-slate-100">
                    <div className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 text-emerald-500">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="block text-[13px] font-bold text-slate-900">Sınırsız Grup Özgürlüğü</span>
                      <span className="block text-[11.5px] text-slate-500 font-medium">İstediğiniz kadar gruba katılın veya kurun</span>
                    </div>
                  </div>
                  {/* 2. Özel Saat Belirleme */}
                  <div className="flex items-center gap-3.5 text-left bg-slate-50 p-2.5 rounded-2xl border border-slate-100">
                    <div className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 text-emerald-500">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </div>
                    <div>
                      <span className="block text-[13px] font-bold text-slate-900">Özel Çekiliş Saati</span>
                      <span className="block text-[11.5px] text-slate-500 font-medium">Çekiliş programınızı size en uygun saate ayarlayın</span>
                    </div>
                  </div>
                  {/* 3. Reklamsız Deneyim */}
                  <div className="flex items-center gap-3.5 text-left bg-slate-50 p-2.5 rounded-2xl border border-slate-100">
                    <div className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 text-emerald-500">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="block text-[13px] font-bold text-slate-900">Reklamsız Deneyim</span>
                      <span className="block text-[11.5px] text-slate-500 font-medium">Sadece hedefinize odaklanın, reklamlara son</span>
                    </div>
                  </div>
                  {/* 4. Hızlı Destek */}
                  <div className="flex items-center gap-3.5 text-left bg-slate-50 p-2.5 rounded-2xl border border-slate-100">
                    <div className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 text-emerald-500">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    </div>
                    <div>
                      <span className="block text-[13px] font-bold text-slate-900">Öncelikli Hızlı Destek</span>
                      <span className="block text-[11.5px] text-slate-500 font-medium">Her türlü sorunuzda anında öncelikli yanıt alın</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-4 mb-6">
                  <button
                    onClick={() => {
                      closeModal();
                      navigate('/premium');
                    }}
                    className="w-full h-14 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold rounded-2xl shadow-[0_8px_20px_-6px_rgba(16,185,129,0.4)] flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                  >
                    <Sparkle className="w-5 h-5" />
                    Premium Ayrıcalıklarını Keşfet
                  </button>
                  <button
                    onClick={closeModal}
                    className="w-full text-[13px] font-bold text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    Hayır, sınırlı bir şekilde devam et
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
